import React, { useState } from 'react';
import {
  MenuItem,
  Select,
  Grid,
  FormControl,
  Box,
  CircularProgress,
  Badge,
  Button,
  InputLabel,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { COLOR_PRIMARY_2_800, COLOR_PRIMARY_2_200, COLOR_TEXT_GREY } from 'src/utils/colors';
import TooltipNew from '../Tooltips/TooltipNew';

interface SubmarketDashboardControlProps {
  selectedBedroom: string | null;
  setSelectedBedroom: (value: string | null) => void;
  hiddenSubmarkets: {
    data: string[];
    loading: boolean;
  };
  setShowHiddenDrawer: (value: boolean) => void;
  setShowSubmarketCreationModal: (value: boolean) => void;
  setSubmarketCreationSource: (source: 'submarkets-tab' | 'map') => void;
  fetchSubMarketData: (sortBy: string | undefined, bedroomType: string | null) => void;
  isSubmarketAccessAllowed: boolean;
  handleOnSampleClick: (plan: string) => void;
  submarkets: any;
  handleExport: () => void;
  isMobileScreen: boolean;
  heatmapMode: string;
}

const SubmarketDashboardControl = ({
  selectedBedroom,
  setSelectedBedroom,
  submarkets,
  hiddenSubmarkets,
  setShowHiddenDrawer,
  setShowSubmarketCreationModal,
  setSubmarketCreationSource,
  fetchSubMarketData,
  isSubmarketAccessAllowed,
  handleOnSampleClick,
  handleExport,
  isMobileScreen,
  heatmapMode,
}: SubmarketDashboardControlProps) => {
  const [selectOpen, setSelectOpen] = useState(false);

  return (
    <>
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
        <Grid item>
          <TooltipNew
            title=""
            content="The bedroom filter currently works only with submarkets and is not supported for Smart Zones."
            disable={heatmapMode === 'submarkets'}
            placement="left-start"
          >
            <FormControl sx={{ width: '150px' }} size="small" disabled={heatmapMode === 'smartZones'}>
              <InputLabel size="small">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    width: '100%',
                    color: COLOR_TEXT_GREY,
                  }}
                >
                  <FilterAltOutlinedIcon sx={{ fontSize: '16px', mr: 0.5 }} />
                  Bedrooms
                </Box>
              </InputLabel>
              <Select
                value={selectedBedroom}
                label="Bedrooms"
                size="small"
                open={selectOpen}
                onClose={() => setSelectOpen(false)}
                onOpen={() => setSelectOpen(true)}
                onChange={event => {
                  setSelectedBedroom(event.target.value as string);
                  if (submarkets && submarkets.length === 0) {
                    return;
                  }
                  if (isSubmarketAccessAllowed) {
                    fetchSubMarketData(undefined, event.target.value as string);
                  } else {
                    handleOnSampleClick('expert');
                  }
                }}
              >
                {[
                  ['All', 'all'],
                  ['Studio', 0],
                  [1, 1],
                  [2, 2],
                  [3, 3],
                  [4, 4],
                  [5, 5],
                  ['6+', 6],
                ].map(bedroom => (
                  <MenuItem value={bedroom[1]}>
                    {bedroom[0]}{' '}
                    {!isSubmarketAccessAllowed && bedroom[1] !== 'all' && (
                      <LockIcon sx={{ fontSize: '16px', transform: 'translateY(2px)' }} />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </TooltipNew>
        </Grid>

        <Grid item>
          <Button
            variant="outlined"
            onClick={handleExport}
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{
              color: COLOR_PRIMARY_2_800,
              background: COLOR_PRIMARY_2_200 + '22',
              fontWeight: 600,
              height: '40px',
              borderColor: 'rgba(0, 0, 0, 0.23)',
              minWidth: '100px',
            }}
          >
            Export
          </Button>
        </Grid>

        {!isMobileScreen && hiddenSubmarkets.data.length > 0 && (
          <Grid item>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Badge
                badgeContent={
                  hiddenSubmarkets.loading ? (
                    <CircularProgress size={10} sx={{ color: 'white' }} />
                  ) : (
                    hiddenSubmarkets.data.length
                  )
                }
                color="primary"
              >
                <Button
                  variant="outlined"
                  onClick={() => setShowHiddenDrawer(true)}
                  startIcon={<VisibilityOffIcon />}
                  disabled={hiddenSubmarkets.loading}
                >
                  Hidden Submarkets
                </Button>
              </Badge>
            </Box>
          </Grid>
        )}
      </Box>
    </>
  );
};

export default SubmarketDashboardControl;
